import {
    Fragment,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from "react";
import { useNavigate } from "react-router-dom";
import swal from "sweetalert";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import DownloadIcon from "@mui/icons-material/Download";
import moneyExchangeIcon from "../../Assets/Images/transaction-data.png";
import sampleIcon from "../../Assets/Images/info.png";
import { UserAuthContext } from "../../Contexts/UserAuthContext";
import "./MotionVideo.css";


/* =========================================================
   CONFIGURATION
========================================================= */
const API_BASE_URL = "https://backend.whalesai.com";
const PROMPT_CHECK_ENDPOINT = `${API_BASE_URL}/api/v1/prompt-checker/check`;
const GENERATE_ENDPOINT = `${API_BASE_URL}/api/v1/motion-video-ai/create`;
const SESSION_ENDPOINT = `${API_BASE_URL}/api/v1/motion-video-ai`;

const ACTIVE_STATUS_ENDPOINT = `${SESSION_ENDPOINT}/active-status`;
const MARK_VIEWED_ENDPOINT = `${SESSION_ENDPOINT}/mark-viewed`;

const ACTIVE_JOB_KEY = "active_motion_video_ai";

const MAX_REFERENCE_VIDEO_SECONDS = 600; // original uploaded video
const MAX_TRIM_SECONDS = 15; // selected portion only
const MIN_TRIM_SECONDS = 1;
const MAX_CHARACTERS = 4; // configurable: one reference image per character
const MAX_VIDEO_SIZE_MB = 500;
const MAX_IMAGE_SIZE_MB = 10;
const MAX_PROMPT_LENGTH = 2000;
const MIN_SELECTION_PERCENT = 2;
const THUMBNAIL_COUNT = 12;
const POLL_INTERVAL_MS = 4000;
const JOB_MAX_AGE_MS = 2 * 60 * 60 * 1000;
const FRAME_TOLERANCE = 0.1;
const CHARACTER_BOX_COLOR = "#ff0000";

const IMAGE_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
const VIDEO_TYPES = ["video/mp4", "video/webm"];
const RESOLUTIONS = [
    { value: "480p", label: "480p", tag: "SD", creditsPerSecond: 20 },
    { value: "720p", label: "720p", tag: "HD", creditsPerSecond: 46 },
    { value: "1080p", label: "1080p", tag: "FHD", creditsPerSecond: 52 },
];
const ASPECTS = [
    { value: "16:9", label: "Widescreen" },
    { value: "9:16", label: "Vertical" },
];

/* =========================================================
   HELPERS
========================================================= */
const authHeader = () => ({
    Authorization: `Bearer ${localStorage.getItem("aiUser") || ""}`,
});

const markMotionVideoViewed = async (transactionId) => {
    if (!transactionId) return;

    try {
        const response = await fetch(
            MARK_VIEWED_ENDPOINT,
            {
                method: "POST",
                headers: {
                    ...authHeader(),
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
                body: JSON.stringify({
                    transactionId,
                }),
            }
        );

        const data = await response.json().catch(() => null);

        console.log(
            "[Motion Video] Mark viewed response:",
            data
        );

    } catch (error) {
        console.error(
            "[Motion Video] Mark viewed error:",
            error
        );
    }
};

const showAlert = (text, icon = "warning") =>
    swal({ text, icon, buttons: "Ok", className: "modal_class_success" });

const confirmAction = (text, confirmLabel) =>
    swal({
        text,
        icon: "warning",
        buttons: ["Cancel", confirmLabel],
        className: "modal_class_success",
    });

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const revokeUrl = (url) => {
    if (!url) return;
    try {
        URL.revokeObjectURL(url);
    } catch {
        // Ignore already-revoked URLs.
    }
};

const formatTime = (seconds) => {
    const total = Math.max(0, Math.floor(Number(seconds) || 0));
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
};

const formatSize = (bytes) => {
    if (!bytes) return "0 MB";
    return bytes >= 1024 * 1024 * 1024
        ? `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`
        : `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const fileExtension = (file) => (file.name.split(".").pop() || "").toLowerCase();

const isVideoFile = (file) =>
    VIDEO_TYPES.includes(file.type) ||
    (!file.type && ["mp4", "webm"].includes(fileExtension(file)));

const isImageFile = (file) => IMAGE_TYPES.includes(file.type);

const isHttpUrl = (value) => {
    try {
        const url = new URL(value);
        return url.protocol === "http:" || url.protocol === "https:";
    } catch {
        return false;
    }
};

const joinList = (items) =>
    items.length <= 1
        ? items.join("")
        : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;

const buildPromptExample = (count) => {
    const n = Math.max(1, count);
    const numbers = Array.from({ length: n }, (_, i) => i + 1);
    const intro = numbers.map((i) => `@Image${i} is Person ${i}`).join(", ");
    const swaps = joinList(numbers.map((i) => `Person ${i} with @Image${i}`));
    return `${intro}. In @Video1, replace ${swaps}.`;
};

const getStoredJob = () => {
    try {
        const job = JSON.parse(localStorage.getItem(ACTIVE_JOB_KEY) || "null");
        if (
            !job ||
            typeof job !== "object" ||
            (!job.transactionId && !job.sessionId)
        ) {
            localStorage.removeItem(ACTIVE_JOB_KEY);
            return null;
        }
        const started = Date.parse(job.startedAt);
        if (Number.isFinite(started) && Date.now() - started > JOB_MAX_AGE_MS) {
            localStorage.removeItem(ACTIVE_JOB_KEY);
            return null;
        }
        return job;
    } catch {
        try {
            localStorage.removeItem(ACTIVE_JOB_KEY);
        } catch {
            // Ignore storage errors.
        }
        return null;
    }
};

const normalizeResult = (payload, job = {}) => {
    const data = payload?.data || payload?.session || payload || {};
    const videoUrl = data.videoUrl || data.outputUrl;
    if (!videoUrl || typeof videoUrl !== "string") return null;

    const creditsUsed =
        data.creditsUsed != null ? Number(data.creditsUsed) : null;
    let credits = "";
    if (creditsUsed != null && Number.isFinite(creditsUsed)) {
        credits = `${creditsUsed.toLocaleString()} credits`;
    } else if (data.cost != null) {
        credits = `$${Number(data.cost).toFixed(2)}`;
    } else if (data.costUsd != null) {
        credits = `$${Number(data.costUsd).toFixed(2)}`;
    } else if (job.estimatedCredits != null) {
        credits = `${Number(job.estimatedCredits).toLocaleString()} credits`;
    }

    return {
        transactionId: data.transactionId || job.transactionId || "",
        sessionId: data.sessionId || data._id || job.sessionId || "",
        videoUrl,
        credits,
        duration: data.duration ?? job.duration ?? null,
        resolution: data.resolution || job.resolution || "",
        aspectRatio: data.aspectRatio || job.aspectRatio || "",
        status: data.status || "completed",
    };
};

/* Reads duration/size of a video. Handles WEBM files that report Infinity. */
const readVideoMetadata = (url) =>
    new Promise((resolve, reject) => {
        const el = document.createElement("video");
        let timer = null;
        const cleanup = () => {
            window.clearTimeout(timer);
            el.onloadedmetadata = null;
            el.onerror = null;
            el.ontimeupdate = null;
            el.removeAttribute("src");
            el.load();
        };
        const done = () => {
            const meta = {
                duration: el.duration,
                width: el.videoWidth,
                height: el.videoHeight,
            };
            cleanup();
            resolve(meta);
        };
        timer = window.setTimeout(() => {
            cleanup();
            reject(new Error("timeout"));
        }, 20000);
        el.preload = "metadata";
        el.muted = true;
        el.onerror = () => {
            cleanup();
            reject(new Error("unreadable"));
        };
        el.onloadedmetadata = () => {
            if (el.duration === Infinity) {
                el.ontimeupdate = () => {
                    if (Number.isFinite(el.duration)) done();
                };
                el.currentTime = 1e101;
                return;
            }
            done();
        };
        el.src = url;
    });

/* Filmstrip thumbnails (cover-cropped, never stretched). */
const generateVideoThumbnails = (url, duration, count, isCancelled) =>
    new Promise((resolve) => {
        const el = document.createElement("video");
        const canvas = document.createElement("canvas");
        canvas.width = 120;
        canvas.height = 68;
        const ctx = canvas.getContext("2d");
        const frames = [];
        let finished = false;

        const finish = () => {
            if (finished) return;
            finished = true;
            el.onloadeddata = null;
            el.onerror = null;
            el.onseeked = null;
            el.removeAttribute("src");
            el.load();
            resolve(frames);
        };

        const seekTo = (time) =>
            new Promise((done) => {
                const timer = window.setTimeout(() => {
                    el.onseeked = null;
                    done(false);
                }, 4000);
                el.onseeked = () => {
                    window.clearTimeout(timer);
                    el.onseeked = null;
                    done(true);
                };
                el.currentTime = time;
            });

        const run = async () => {
            for (let i = 0; i < count; i += 1) {
                if (finished || isCancelled()) break;
                const time = Math.min(
                    (duration * (i + 0.5)) / count,
                    Math.max(0, duration - 0.1)
                );
                const ok = await seekTo(time);
                const vw = el.videoWidth;
                const vh = el.videoHeight;
                if (!ok || !vw || !vh || !ctx) continue;
                try {
                    const scale = Math.max(canvas.width / vw, canvas.height / vh);
                    const sw = canvas.width / scale;
                    const sh = canvas.height / scale;
                    ctx.drawImage(
                        el,
                        (vw - sw) / 2,
                        (vh - sh) / 2,
                        sw,
                        sh,
                        0,
                        0,
                        canvas.width,
                        canvas.height
                    );
                    frames.push(canvas.toDataURL("image/jpeg", 0.65));
                } catch {
                    // Skip frames that cannot be drawn.
                }
            }
            finish();
        };

        el.preload = "auto";
        el.muted = true;
        el.playsInline = true;
        el.onloadeddata = run;
        el.onerror = finish;
        el.src = url;
    });

/* Keeps a player inside the trimmed segment. */
const clampPlayer = (player, start, end) => {
    if (player.currentTime > end) {
        player.pause();
        player.currentTime = end;
    } else if (player.currentTime < start - 0.05) {
        player.currentTime = start;
    }
};

/* Computes a new trim range. Never exceeds MAX_TRIM_SECONDS. */
const computeTrim = (target, value, cur, maxTime) => {
    if (target === "start") {
        const min = Math.max(0, cur.end - MAX_TRIM_SECONDS);
        const start = clamp(value, min, cur.end - MIN_TRIM_SECONDS);
        return { trim: { start, end: cur.end }, limited: value < min };
    }
    if (target === "end") {
        const max = Math.min(maxTime, cur.start + MAX_TRIM_SECONDS);
        const end = clamp(value, cur.start + MIN_TRIM_SECONDS, max);
        return {
            trim: { start: cur.start, end },
            limited: value > cur.start + MAX_TRIM_SECONDS,
        };
    }
    const length = cur.end - cur.start;
    const start = clamp(value, 0, Math.max(0, maxTime - length));
    return { trim: { start, end: start + length }, limited: false };
};

/* =========================================================
   SMALL PRESENTATION COMPONENTS
========================================================= */
function StepHeader({ number, title }) {
    return (
        <div className="mv-step-header">
            <span className="mv-step-badge">{number}</span>
            <span className="mv-step-title">{title}</span>
        </div>
    );
}

/* =========================================================
   STEP 2 — TRIM
========================================================= */
function TrimStep({ video, trim, thumbnails, disabled, onChange, onCommit }) {
    const timelineRef = useRef(null);
    const previewRef = useRef(null);
    const dragRef = useRef(null);
    const trimRef = useRef(trim);
    const [limitMessage, setLimitMessage] = useState("");

    trimRef.current = trim;
    const duration = video.duration;
    const maxTime = Math.floor(duration);
    const length = trim.end - trim.start;

    const timeFromEvent = (event) => {
        const track = timelineRef.current;
        if (!track) return 0;
        const rect = track.getBoundingClientRect();
        if (!rect.width) return 0;
        return clamp((event.clientX - rect.left) / rect.width, 0, 1) * duration;
    };

    const apply = (target, value) => {
        const { trim: next, limited } = computeTrim(
            target,
            value,
            trimRef.current,
            maxTime
        );
        setLimitMessage(
            limited
                ? `You can select a maximum of ${MAX_TRIM_SECONDS} seconds.`
                : ""
        );
        if (
            next.start !== trimRef.current.start ||
            next.end !== trimRef.current.end
        ) {
            trimRef.current = next;
            onChange(next);
        }
    };

    const handlePointerDown = (event, target) => {
        if (disabled) return;
        event.preventDefault();
        event.stopPropagation();
        event.currentTarget.setPointerCapture(event.pointerId);
        dragRef.current = {
            target,
            grab: timeFromEvent(event) - trimRef.current.start,
        };
        setLimitMessage("");
    };

    const handlePointerMove = (event) => {
        const drag = dragRef.current;
        if (!drag) return;
        const time = timeFromEvent(event);
        if (drag.target === "move") {
            apply("move", Math.round(time - drag.grab));
        } else {
            apply(drag.target, Math.round(time));
        }
    };

    const handlePointerUp = () => {
        if (!dragRef.current) return;
        dragRef.current = null;
        onCommit(trimRef.current);
    };

    const handleKeyDown = (event, target) => {
        if (disabled) return;
        const delta =
            event.key === "ArrowLeft" ? -1 : event.key === "ArrowRight" ? 1 : 0;
        if (!delta) return;
        event.preventDefault();
        const cur = trimRef.current;
        apply(target, (target === "start" ? cur.start : cur.end) + delta);
        onCommit(trimRef.current);
    };

    const handleReset = () => {
        const next = {
            start: 0,
            end: Math.min(maxTime, MAX_TRIM_SECONDS),
        };
        trimRef.current = next;
        setLimitMessage("");
        onChange(next);
        onCommit(next);
    };

    useEffect(() => {
        const player = previewRef.current;
        if (player && player.readyState >= 1) {
            player.currentTime = trim.start;
        }
    }, [trim.start, trim.end]);

    return (
        <div className="mv-step">
            <StepHeader number={2} title="Trim Reference Video" />
            <label className="mv-label">
                Selected Segment{" "}
                <span className="mv-label-note">
                    (any portion up to {MAX_TRIM_SECONDS} seconds)
                </span>
            </label>

            <div className="mv-video-card">
                <video
                    ref={previewRef}
                    src={video.url}
                    controls
                    playsInline
                    preload="metadata"
                    onLoadedMetadata={(event) => {
                        event.currentTarget.currentTime = trimRef.current.start;
                    }}
                    onPlay={(event) => {
                        const player = event.currentTarget;
                        if (player.currentTime >= trimRef.current.end - 0.05) {
                            player.currentTime = trimRef.current.start;
                        }
                    }}
                    onTimeUpdate={(event) =>
                        clampPlayer(
                            event.currentTarget,
                            trimRef.current.start,
                            trimRef.current.end
                        )
                    }
                    onSeeking={(event) =>
                        clampPlayer(
                            event.currentTarget,
                            trimRef.current.start,
                            trimRef.current.end
                        )
                    }
                />

                <div className="mv-start-time">
                    <div className="mv-selection-header">
                        <div>
                            <h4>Trim Reference Video</h4>
                            <p>
                                Drag the yellow handles, or drag the selected
                                area, to choose a video segment.
                            </p>
                        </div>
                        <span className="mv-duration-badge">{length}s</span>
                    </div>

                    <div
                        className="mv-timeline"
                        ref={timelineRef}
                        onPointerMove={handlePointerMove}
                        onPointerUp={handlePointerUp}
                        onPointerCancel={handlePointerUp}
                    >
                        <div className="mv-filmstrip">
                            {thumbnails.map((frame, index) => (
                                <img
                                    key={index}
                                    src={frame}
                                    alt=""
                                    draggable="false"
                                />
                            ))}
                        </div>

                        <div
                            className="mv-timeline-dim left"
                            style={{ width: `${(trim.start / duration) * 100}%` }}
                        />
                        <div
                            className="mv-timeline-dim right"
                            style={{ left: `${(trim.end / duration) * 100}%` }}
                        />

                        <div
                            className={`mv-selection-box ${disabled ? "is-disabled" : ""}`}
                            style={{
                                left: `${(trim.start / duration) * 100}%`,
                                width: `${(length / duration) * 100}%`,
                            }}
                            onPointerDown={(event) =>
                                handlePointerDown(event, "move")
                            }
                        >
                            <button
                                type="button"
                                className="mv-timeline-handle start"
                                aria-label="Adjust start time"
                                disabled={disabled}
                                onPointerDown={(event) =>
                                    handlePointerDown(event, "start")
                                }
                                onKeyDown={(event) => handleKeyDown(event, "start")}
                            >
                                <span />
                            </button>
                            <button
                                type="button"
                                className="mv-timeline-handle end"
                                aria-label="Adjust end time"
                                disabled={disabled}
                                onPointerDown={(event) =>
                                    handlePointerDown(event, "end")
                                }
                                onKeyDown={(event) => handleKeyDown(event, "end")}
                            >
                                <span />
                            </button>
                        </div>
                    </div>

                    <div className="mv-selection-footer">
                        <span>Start: {trim.start}s ({formatTime(trim.start)})</span>
                        <span>End: {trim.end}s ({formatTime(trim.end)})</span>
                        <span>
                            Selected: {length}s / {MAX_TRIM_SECONDS}s max
                        </span>
                    </div>

                    {limitMessage && (
                        <p className="mv-trim-limit" role="status">
                            {limitMessage}
                        </p>
                    )}

                    <button
                        type="button"
                        className="mv-link-btn"
                        disabled={disabled}
                        onClick={handleReset}
                    >
                        Reset selection
                    </button>
                </div>
            </div>
        </div>
    );
}

/* =========================================================
   STEP 3 — CHARACTER SELECTION (works on the trimmed portion)
========================================================= */
function CharacterStep({
    video,
    trim,
    characters,
    disabled,
    onAdd,
    onRemove,
}) {
    const playerRef = useRef(null);
    const overlayRef = useRef(null);
    const startPointRef = useRef(null);
    const countRef = useRef(characters.length);
    const [selectionBox, setSelectionBox] = useState(null); // fractions of the video frame (0-1)
    const [isDrawing, setIsDrawing] = useState(false);
    const [selectionMode, setSelectionMode] = useState(false);
    const [currentTime, setCurrentTime] = useState(trim.start);
    const [isMarking, setIsMarking] = useState(false);
    const [frameRect, setFrameRect] = useState({ l: 0, t: 0, w: 1, h: 1 });

    countRef.current = characters.length;

    useEffect(() => {
        const player = playerRef.current;
        if (
            player &&
            player.readyState >= 1 &&
            (player.currentTime < trim.start || player.currentTime > trim.end)
        ) {
            player.currentTime = trim.start;
        }
        setSelectionBox(null);
        setIsDrawing(false);
        startPointRef.current = null;
    }, [trim.start, trim.end]);

    const limitReached = characters.length >= MAX_CHARACTERS;

    /* The <video> uses object-fit: contain, so the real frame can be letterboxed.
       Track where the frame sits inside the element for accurate cropping. */
    const updateFrameRect = useCallback(() => {
        const player = playerRef.current;
        if (!player || !player.videoWidth || !player.clientWidth) return;
        const ew = player.clientWidth;
        const eh = player.clientHeight;
        const scale = Math.min(ew / player.videoWidth, eh / player.videoHeight);
        const w = player.videoWidth * scale;
        const h = player.videoHeight * scale;
        setFrameRect({
            l: (ew - w) / 2 / ew,
            t: (eh - h) / 2 / eh,
            w: w / ew,
            h: h / eh,
        });
    }, []);

    useEffect(() => {
        const player = playerRef.current;
        if (!player || typeof ResizeObserver === "undefined") return undefined;
        const observer = new ResizeObserver(updateFrameRect);
        observer.observe(player);
        return () => observer.disconnect();
    }, [updateFrameRect]);

    const framePoint = (event) => {
        const overlay = overlayRef.current;
        if (!overlay) return null;
        const rect = overlay.getBoundingClientRect();
        if (!rect.width || !rect.height) return null;
        return {
            x: clamp(
                ((event.clientX - rect.left) / rect.width - frameRect.l) / frameRect.w,
                0,
                1
            ),
            y: clamp(
                ((event.clientY - rect.top) / rect.height - frameRect.t) / frameRect.h,
                0,
                1
            ),
        };
    };

    const resetSelection = () => {
        setSelectionBox(null);
        setIsDrawing(false);
        startPointRef.current = null;
    };

    const handleDown = (event) => {
        if (disabled || !selectionMode || limitReached) return;
        const player = playerRef.current;
        const point = framePoint(event);
        if (!player || !point) return;
        player.pause();
        startPointRef.current = point;
        setSelectionBox({ x: point.x, y: point.y, w: 0, h: 0 });
        setIsDrawing(true);
        event.currentTarget.setPointerCapture(event.pointerId);
    };

    const handleMove = (event) => {
        if (!isDrawing || !startPointRef.current) return;
        const point = framePoint(event);
        if (!point) return;
        const start = startPointRef.current;
        setSelectionBox({
            x: Math.min(start.x, point.x),
            y: Math.min(start.y, point.y),
            w: Math.abs(point.x - start.x),
            h: Math.abs(point.y - start.y),
        });
    };

    const handleUp = () => {
        setIsDrawing(false);
        startPointRef.current = null;
    };

    const handleMark = () => {
        const player = playerRef.current;
        if (!player || !selectionBox) {
            showAlert("Please select a character from the video.");
            return;
        }
        if (countRef.current >= MAX_CHARACTERS) {
            showAlert(`You can select a maximum of ${MAX_CHARACTERS} characters.`);
            return;
        }
        if (
            selectionBox.w * 100 < MIN_SELECTION_PERCENT ||
            selectionBox.h * 100 < MIN_SELECTION_PERCENT
        ) {
            showAlert("Please draw a larger selection around the character.");
            return;
        }
        if (player.readyState < 2 || !player.videoWidth) {
            showAlert("The video frame is not ready yet. Please try again.");
            return;
        }
        if (player.seeking) {
            showAlert("Please wait for the video to finish seeking.");
            return;
        }
        const time = player.currentTime;
        if (
            time < trim.start - FRAME_TOLERANCE ||
            time > trim.end + FRAME_TOLERANCE
        ) {
            showAlert("The selected frame is outside the trimmed portion.");
            return;
        }

        const vw = player.videoWidth;
        const vh = player.videoHeight;
        const sx = Math.min(vw - 1, Math.max(0, Math.round(selectionBox.x * vw)));
        const sy = Math.min(vh - 1, Math.max(0, Math.round(selectionBox.y * vh)));
        const sw = Math.max(1, Math.min(vw - sx, Math.round(selectionBox.w * vw)));
        const sh = Math.max(1, Math.min(vh - sy, Math.round(selectionBox.h * vh)));

        const canvas = document.createElement("canvas");
        // canvas.width = sw;
        // canvas.height = sh;
        canvas.width = vw;
        canvas.height = vh;
        const context = canvas.getContext("2d");
        if (!context) {
            showAlert("Unable to process the selected character.");
            return;
        }

        setIsMarking(true);
        try {
            // 1:1 crop (canvas size equals source size) so nothing is stretched.
            // context.drawImage(player, sx, sy, sw, sh, 0, 0, sw, sh);
            context.drawImage(player, 0, 0, vw, vh);
            context.strokeStyle = CHARACTER_BOX_COLOR;
            context.lineWidth = Math.max(3, Math.round(Math.min(vw, vh) * 0.006));
            context.strokeRect(sx, sy, sw, sh);
            
            const timestamp = Number(clamp(time, trim.start, trim.end).toFixed(2));
            const box = {
                x: Number((selectionBox.x * 100).toFixed(2)),
                y: Number((selectionBox.y * 100).toFixed(2)),
                width: Number((selectionBox.w * 100).toFixed(2)),
                height: Number((selectionBox.h * 100).toFixed(2)),
            };

            canvas.toBlob(
                (blob) => {
                    setIsMarking(false);
                    if (!blob) {
                        showAlert("Could not crop the selected character.");
                        return;
                    }
                    if (countRef.current >= MAX_CHARACTERS) {
                        showAlert(
                            `You can select a maximum of ${MAX_CHARACTERS} characters.`
                        );
                        return;
                    }
                    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
                    onAdd({
                        id,
                        file: new File([blob], `character-${id}.jpg`, {
                            type: "image/jpeg",
                        }),
                        preview: URL.createObjectURL(blob),
                        timestamp, // original-video time
                        relativeTimestamp: Number((timestamp - trim.start).toFixed(2)),
                        boundingBox: box, // percent of the video frame
                    });
                    resetSelection();
                    setSelectionMode(false);
                },
                "image/jpeg",
                0.95
            );
        } catch (error) {
            console.error("Character crop error:", error);
            setIsMarking(false);
            showAlert("Failed to crop the selected character.");
        }
    };

    const boxStyle = selectionBox && {
        left: `${(frameRect.l + selectionBox.x * frameRect.w) * 100}%`,
        top: `${(frameRect.t + selectionBox.y * frameRect.h) * 100}%`,
        width: `${selectionBox.w * frameRect.w * 100}%`,
        height: `${selectionBox.h * frameRect.h * 100}%`,
    };

    return (
        <div className="mv-step">
            <StepHeader number={3} title="Select & Mark Characters" />
            <div className="mv-character-section">
                <div className="mv-selection-header">
                    <div>
                        <h4>Select Characters</h4>
                        <p>
                            Pause the video and draw a box around each character
                            you want to replace. Only the trimmed{" "}
                            {trim.end - trim.start}s segment ({formatTime(trim.start)}
                            –{formatTime(trim.end)}) is available.
                        </p>
                    </div>
                    <span className="mv-duration-badge">
                        {characters.length}/{MAX_CHARACTERS} Selected
                    </span>
                </div>

                <div className="mv-character-player">
                    <div className="mv-character-video-wrap">
                        <video
                            ref={playerRef}
                            src={video.url}
                            controls
                            playsInline
                            preload="metadata"
                            onLoadedMetadata={(event) => {
                                event.currentTarget.currentTime = trim.start;
                                updateFrameRect();
                            }}
                            onPlay={(event) => {
                                const player = event.currentTarget;
                                if (player.currentTime >= trim.end - 0.05) {
                                    player.currentTime = trim.start;
                                }
                            }}
                            onTimeUpdate={(event) => {
                                const player = event.currentTarget;
                                clampPlayer(player, trim.start, trim.end);
                                setCurrentTime(player.currentTime);
                            }}
                            onSeeking={(event) =>
                                clampPlayer(event.currentTarget, trim.start, trim.end)
                            }
                            onSeeked={(event) =>
                                setCurrentTime(event.currentTarget.currentTime)
                            }
                        />

                        <div
                            ref={overlayRef}
                            className={`mv-character-overlay ${selectionMode ? "is-active" : ""}`}
                            onPointerDown={handleDown}
                            onPointerMove={handleMove}
                            onPointerUp={handleUp}
                            onPointerCancel={handleUp}
                        >
                            {selectionBox && (
                                <div
                                    className="mv-character-selection-box"
                                    style={boxStyle}
                                >
                                    <span>Character Selection</span>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="mv-character-instructions">
                        {selectionMode ? (
                            <p>
                                <strong>Selection mode active:</strong> Drag around
                                a character to mark it.
                            </p>
                        ) : (
                            <p>
                                Pause the video at a frame where the character is
                                clearly visible, then start selecting.
                            </p>
                        )}
                        <span>Current time: {currentTime.toFixed(2)}s</span>
                    </div>

                    <div className="mv-character-actions">
                        <button
                            type="button"
                            className="mv-character-select-btn"
                            disabled={disabled || limitReached}
                            onClick={() => {
                                const player = playerRef.current;
                                if (!player) return;
                                if (limitReached) {
                                    showAlert(
                                        `You can select a maximum of ${MAX_CHARACTERS} characters.`
                                    );
                                    return;
                                }
                                player.pause();
                                setSelectionBox(null);
                                setSelectionMode(true);
                            }}
                        >
                            {selectionMode ? "Selection Mode Active" : "Select Character"}
                        </button>

                        <button
                            type="button"
                            className="mv-character-mark-btn"
                            disabled={
                                disabled || isMarking || !selectionBox || !selectionMode
                            }
                            onClick={handleMark}
                        >
                            Mark Character
                        </button>

                        <button
                            type="button"
                            className="mv-character-cancel-btn"
                            disabled={!selectionBox && !selectionMode}
                            onClick={() => {
                                resetSelection();
                                setSelectionMode(false);
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                </div>

                <div className="mv-selected-characters">
                    <div className="mv-selected-header">
                        <h4>Marked Characters</h4>
                        <span>
                            {characters.length} / {MAX_CHARACTERS}
                        </span>
                    </div>

                    {characters.length === 0 ? (
                        <div className="mv-character-empty">
                            <p>No characters selected yet.</p>
                            <span>
                                Select a character from the video and click Mark
                                Character.
                            </span>
                        </div>
                    ) : (
                        <div className="mv-character-grid">
                            {characters.map((character, index) => (
                                <div className="mv-character-card" key={character.id}>
                                    <div className="mv-character-image">
                                        <img
                                            src={character.preview}
                                            alt={`Character ${index + 1}`}
                                        />
                                        <span>Character {index + 1}</span>
                                    </div>
                                    <div className="mv-character-card-info">
                                        <strong>Character {index + 1}</strong>
                                        <span>{character.timestamp}s</span>
                                    </div>
                                    <button
                                        type="button"
                                        className="mv-character-remove"
                                        disabled={disabled}
                                        onClick={() => onRemove(character.id)}
                                    >
                                        Remove
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {limitReached && (
                        <p className="mv-character-limit">
                            Maximum {MAX_CHARACTERS} characters selected.
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}

/* =========================================================
   MAIN COMPONENT
========================================================= */
export default function MotionVideo() {
    const { AIuser, setUserRefetch } = useContext(UserAuthContext);
    const navigate = useNavigate();

    const [video, setVideo] = useState(null);
    const [isReadingVideo, setIsReadingVideo] = useState(false);
    const [thumbnails, setThumbnails] = useState([]);
    const [trim, setTrim] = useState({ start: 0, end: 0 });
    const [characters, setCharacters] = useState([]);
    const [referenceImages, setReferenceImages] = useState({}); // { [characterId]: { file, preview } }
    const [prompt, setPrompt] = useState("");
    const [resolution, setResolution] = useState("1080p");
    const [aspectRatio, setAspectRatio] = useState("16:9");

    const [isLoading, setIsLoading] = useState(false);
    const [loadingStep, setLoadingStep] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [notice, setNotice] = useState("");
    const [result, setResult] = useState(null);
    const [resultVideoFailed, setResultVideoFailed] = useState(false);
    const [activeJob, setActiveJob] = useState(null);
    const [isDownloading, setIsDownloading] = useState(false);

    const progressRef = useRef(null);
    const resultRef = useRef(null);
    const errorRef = useRef(null);
    const submittingRef = useRef(false);
    const readingRef = useRef(false);
    const activeJobRef = useRef(null);
    const pollInFlightRef = useRef(false);
    const pollNowRef = useRef(null);
    const latestRef = useRef({ video: null, characters: [], referenceImages: {} });
    latestRef.current = { video, characters, referenceImages };

    /* ---------- derived values ---------- */
    const selectedDuration = video ? Math.max(0, trim.end - trim.start) : 0;
    const trimValid =
        Boolean(video) &&
        Number.isInteger(trim.start) &&
        Number.isInteger(trim.end) &&
        trim.start >= 0 &&
        trim.end <= Math.floor(video.duration) &&
        selectedDuration >= MIN_TRIM_SECONDS &&
        selectedDuration <= MAX_TRIM_SECONDS;
    const resolutionConfig = RESOLUTIONS.find((item) => item.value === resolution);
    const creditsPerSecond = resolutionConfig?.creditsPerSecond || 0;
    const estimatedCredits = creditsPerSecond * selectedDuration;
    const creditsKnown = AIuser?.credits != null;
    const availableCredits = creditsKnown ? Number(AIuser.credits) : 0;
    const hasEnoughCredits = creditsKnown && availableCredits >= estimatedCredits;
    const credits = creditsKnown
        ? Math.floor(Number(AIuser.credits)).toLocaleString()
        : "0";
    const uploadedReferenceCount = characters.filter(
        (character) => referenceImages[character.id]
    ).length;
    const trimmedPrompt = prompt.trim();

    /* ---------- validation ---------- */
    const validatePrompt = () => {
        if (!trimmedPrompt) return ""; // prompt is optional
        if (trimmedPrompt.length > MAX_PROMPT_LENGTH) {
            return `Prompt must be ${MAX_PROMPT_LENGTH} characters or fewer.`;
        }
        const imageRefs = [...trimmedPrompt.matchAll(/@Image(\d+)/gi)].map((m) =>
            Number(m[1])
        );
        const badImage = imageRefs.find(
            (number) => number < 1 || number > characters.length
        );
        if (badImage !== undefined) {
            return `Your prompt mentions @Image${badImage}, but you have ${characters.length} reference image(s).`;
        }
        const videoRefs = [...trimmedPrompt.matchAll(/@Video(\d+)/gi)].map((m) =>
            Number(m[1])
        );
        if (videoRefs.some((number) => number !== 1)) {
            return "Only @Video1 can be used in the prompt.";
        }
        return "";
    };

    const getStepError = (number) => {
        switch (number) {
            case 1:
                return video ? "" : "Upload a reference video to continue.";
            case 2:
                return trimValid
                    ? ""
                    : `Select a segment between ${MIN_TRIM_SECONDS} and ${MAX_TRIM_SECONDS} seconds to continue.`;
            case 3:
                return characters.length >= 1
                    ? ""
                    : "Mark at least one character to continue.";
            case 4: {
                if (characters.length === 0) {
                    return "Mark at least one character first.";
                }
                const missing = characters.length - uploadedReferenceCount;
                return missing > 0
                    ? `Upload a reference image for ${missing} more character${missing > 1 ? "s" : ""} to continue.`
                    : "";
            }
            case 5:
                return validatePrompt();
            case 6:
                return RESOLUTIONS.some((item) => item.value === resolution) &&
                    ASPECTS.some((item) => item.value === aspectRatio) &&
                    trimValid
                    ? ""
                    : "Please choose valid video settings.";
            default:
                return "";
        }
    };

    const firstInvalidStep = [1, 2, 3, 4, 5, 6].find((n) => getStepError(n));
    const maxReachable = firstInvalidStep || 7;

    /* ---------- active job helpers ---------- */
    const updateActiveJob = useCallback((job) => {
        activeJobRef.current = job;
        setActiveJob(job);
        try {
            if (job) {
                localStorage.setItem(ACTIVE_JOB_KEY, JSON.stringify(job));
            } else {
                localStorage.removeItem(ACTIVE_JOB_KEY);
            }
        } catch {
            // Storage may be unavailable (private mode).
        }
    }, []);

    const refreshCredits = useCallback(() => {
        if (typeof setUserRefetch === "function") {
            setUserRefetch((previous) => !previous);
        }
    }, [setUserRefetch]);

    const finishJob = useCallback(
        (payload, fallback = {}) => {
            const job = activeJobRef.current;
            if (!job) return false; // already handled (prevents duplicate processing)
            const normalized = normalizeResult(payload, { ...fallback, ...job });
            if (!normalized) return false;
            updateActiveJob(null);
            submittingRef.current = false;
            setResult(normalized);
            setResultVideoFailed(false);
            setIsLoading(false);
            setLoadingStep("");
            setErrorMessage("");
            // Mark this completed transaction as viewed
            if (normalized.transactionId) {
                markMotionVideoViewed(normalized.transactionId);
            }
            refreshCredits();
            return true;
        },
        [updateActiveJob, refreshCredits]
    );

    const failJob = useCallback(
        (message) => {
            if (!activeJobRef.current) return;
            const error =
                message || "Motion video generation failed. Please try again.";
            updateActiveJob(null);
            submittingRef.current = false;
            setIsLoading(false);
            setLoadingStep("");
            setErrorMessage(error);
            refreshCredits();
            swal({
                text: error,
                icon: "error",
                buttons: "Ok",
                className: "modal_class_success",
            });
        },
        [updateActiveJob, refreshCredits]
    );

    /* ---------- init, step scrolling, unmount cleanup ---------- */
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        if (isLoading) {
            progressRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [isLoading]);

    useEffect(() => {
        if (result) {
            resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, [result]);

    useEffect(() => {
        if (errorMessage) {
            errorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [errorMessage]);

    useEffect(
        () => () => {
            const state = latestRef.current;
            revokeUrl(state.video?.url);
            state.characters.forEach((character) => revokeUrl(character.preview));
            Object.values(state.referenceImages).forEach((image) =>
                revokeUrl(image.preview)
            );
        },
        []
    );

    /* ---------- thumbnails ---------- */
    useEffect(() => {
        setThumbnails([]);
        if (!video?.url) return undefined;
        let cancelled = false;
        generateVideoThumbnails(
            video.url,
            video.duration,
            THUMBNAIL_COUNT,
            () => cancelled
        ).then((frames) => {
            if (!cancelled) setThumbnails(frames);
        });
        return () => {
            cancelled = true;
        };
    }, [video]);

    /* =========================================================
       WEBSOCKET EVENTS
       Global manager dispatches backend_task_completed /
       backend_task_failed (payload type: motion-video-ai).
    ========================================================= */
    useEffect(() => {
        const getPayload = (event) => {
            const detail = event?.detail || {};
            if (detail.data && !detail.type) {
                return { ...detail.data, ...detail };
            }
            return detail;
        };

        // Accepts an event only when it belongs to the active job:
        // at least one id must match and no id may conflict.
        const matchesActiveJob = (payload) => {
            const job = activeJobRef.current;
            if (!job) return false;
            const data = payload?.data || {};
            const type = String(
                payload?.type || payload?.eventType || data.type || ""
            ).toLowerCase();
            if (type && !type.includes("motion")) return false;

            const transactionId = payload?.transactionId || data.transactionId;
            const sessionId = payload?.sessionId || data.sessionId || data._id;
            let matched = false;

            if (job.transactionId && transactionId) {
                if (String(job.transactionId) !== String(transactionId)) return false;
                matched = true;
            }
            if (job.sessionId && sessionId) {
                if (String(job.sessionId) !== String(sessionId)) return false;
                matched = true;
            }
            return matched;
        };

        const handleCompleted = (event) => {
            const payload = getPayload(event);
            if (!matchesActiveJob(payload)) return;
            const status = String(
                payload.status || payload.data?.status || ""
            ).toLowerCase();

            if (status === "failed") {
                failJob(payload.message);
                return;
            }
            if (status === "processing" || status === "pending") {
                setLoadingStep(
                    payload.message ||
                    "Processing video reference and generating motion video..."
                );
                return;
            }
            if (!finishJob(payload, activeJobRef.current)) {
                setLoadingStep(payload.message || "Your video is being finalized...");
                if (pollNowRef.current) pollNowRef.current();
            }
        };

        const handleFailed = (event) => {
            const payload = getPayload(event);
            if (!matchesActiveJob(payload)) return;
            failJob(payload.message);
        };

        window.addEventListener("backend_task_completed", handleCompleted);
        window.addEventListener("backend_task_failed", handleFailed);
        return () => {
            window.removeEventListener("backend_task_completed", handleCompleted);
            window.removeEventListener("backend_task_failed", handleFailed);
        };
    }, [finishJob, failJob]);

    /* =========================================================
       SESSION RECOVERY (after reload)
    ========================================================= */
    useEffect(() => {
        const stored = getStoredJob();
        if (!stored) return;
        activeJobRef.current = stored;
        submittingRef.current = true;
        setActiveJob(stored);
        setIsLoading(true);
        setLoadingStep("Reconnecting to your motion video generation...");
    }, []);

    /* Polling runs for every active job (fresh or restored) as a fallback
       for missed WebSocket events. It stops when the job ends. */
    // const activeSessionId = activeJob?.sessionId;
    const activeSessionId = activeJob?.sessionId;

    const hasActiveJob = Boolean(
        activeJob?.sessionId ||
        activeJob?.transactionId
    );
    useEffect(() => {
        // if (!activeSessionId) return undefined;
        if (!hasActiveJob) return undefined;
        let cancelled = false;

        const poll = async () => {
            if (cancelled || pollInFlightRef.current) return;
            pollInFlightRef.current = true;
            try {
                // const response = await fetch(
                //     `${SESSION_ENDPOINT}/${encodeURIComponent(activeSessionId)}`,
                //     {
                //         method: "GET",
                //         headers: { ...authHeader(), Accept: "application/json" },
                //     }
                // );

                // const data = await response.json().catch(() => null);
                // const job = activeJobRef.current;
                // if (cancelled || !job || String(job.sessionId) !== String(activeSessionId)) {
                //     return;
                // }
                // if (response.status === 404) {
                //     failJob("This generation session could not be found.");
                //     // swal
                //     return;
                // }
                // if (!response.ok || !data?.status) return;

                // const session = data.session || data.data || {};
                // const status = String(session.status || "").toLowerCase();

                // if (status === "completed" || session.videoUrl) {
                //     if (!finishJob(session, job)) {
                //         setLoadingStep("Your video is being finalized...");
                //     }
                //     return;
                // }
                // if (status === "failed") {
                //     failJob(session.message || "Motion video generation failed.");
                //     return;
                // }
                // setLoadingStep(
                //     session.message ||
                //     "Processing video reference and generating motion video..."
                // );



                const response = await fetch(
                    ACTIVE_STATUS_ENDPOINT,
                    {
                        method: "GET",
                        headers: {
                            ...authHeader(),
                            Accept: "application/json",
                        },
                        cache: "no-store",
                    }
                );

                const result = await response.json().catch(() => null);

                const job = activeJobRef.current;

                // Make sure this response belongs to the current job
                if (
                    cancelled ||
                    !job ||
                    (
                        job.sessionId &&
                        String(activeSessionId) !== String(job.sessionId)
                    )
                ) {
                    return;
                }

                if (!response.ok) {
                    console.error(
                        "[Motion Video] Active status request failed:",
                        response.status,
                        result
                    );

                    return;
                }

                console.log(
                    "[Motion Video] Active status:",
                    result
                );

                // Transaction matching
                if (
                    result?.transactionId &&
                    job.transactionId &&
                    String(result.transactionId) !== String(job.transactionId)
                ) {
                    return;
                }

                // Session matching, when backend returns a session ID
                const responseSessionId =
                    result?.sessionId ||
                    result?.data?.sessionId ||
                    result?.data?._id;

                if (
                    responseSessionId &&
                    job.sessionId &&
                    String(responseSessionId) !== String(job.sessionId)
                ) {
                    return;
                }

                // GENERATING
                if (
                    result?.success &&
                    result?.isGenerating
                ) {
                    setIsLoading(true);

                    setLoadingStep(
                        result.message ||
                        "Processing video reference and generating motion video..."
                    );

                    return;
                }

                // Normalize response data
                const session =
                    result?.data?.session ||
                    result?.session ||
                    result?.data ||
                    result;

                const status = String(
                    result?.status ||
                    session?.status ||
                    ""
                ).toLowerCase();

                const videoUrl =
                    session?.videoUrl ||
                    session?.outputUrl ||
                    result?.videoUrl;

                // COMPLETED
                if (
                    status === "completed" &&
                    videoUrl
                ) {
                    finishJob(
                        {
                            ...result,
                            ...session,
                            transactionId:
                                result?.transactionId ||
                                session?.transactionId ||
                                job.transactionId,

                            sessionId:
                                responseSessionId ||
                                job.sessionId,

                            videoUrl,
                            status: "completed",
                        },
                        job
                    );

                    return;
                }

                // FAILED
                if (
                    status === "failed" ||
                    status === "error"
                ) {
                    failJob(
                        result?.message ||
                        session?.message ||
                        "Motion video generation failed."
                    );

                    return;
                }

                // STILL PROCESSING
                setIsLoading(true);

                setLoadingStep(
                    result?.message ||
                    session?.message ||
                    "Processing video reference and generating motion video..."
                );



            } catch {
                // Temporary network errors: keep waiting for WebSocket / next poll.
            } finally {
                pollInFlightRef.current = false;
            }
        };

        const handleVisibility = () => {
            if (!document.hidden) poll();
        };

        // pollNowRef.current = poll;
        // poll();
        // const timer = window.setInterval(poll, POLL_INTERVAL_MS);

        pollNowRef.current = poll;

        // Wait before the first polling request.
        const initialPollTimer = window.setTimeout(() => {
            poll();
        }, POLL_INTERVAL_MS);

        const timer = window.setInterval(poll, POLL_INTERVAL_MS);

        window.addEventListener("focus", poll);
        window.addEventListener("pageshow", poll);
        document.addEventListener("visibilitychange", handleVisibility);

        // return () => {
        //     cancelled = true;
        //     pollNowRef.current = null;
        //     window.clearInterval(timer);
        //     window.removeEventListener("focus", poll);
        //     window.removeEventListener("pageshow", poll);
        //     document.removeEventListener("visibilitychange", handleVisibility);
        // };
        return () => {
            cancelled = true;
            pollNowRef.current = null;

            window.clearTimeout(initialPollTimer);
            window.clearInterval(timer);

            window.removeEventListener("focus", poll);
            window.removeEventListener("pageshow", poll);
            document.removeEventListener("visibilitychange", handleVisibility);
        };
    }, [hasActiveJob, activeSessionId, finishJob, failJob]);
    // }, [activeSessionId, finishJob, failJob]);

    /* =========================================================
       STATE RESET HELPERS
    ========================================================= */
    const discardCharacters = () => {
        const state = latestRef.current;
        state.characters.forEach((character) => revokeUrl(character.preview));
        Object.values(state.referenceImages).forEach((image) =>
            revokeUrl(image.preview)
        );
        setCharacters([]);
        setReferenceImages({});
    };

    const resetAll = () => {
        revokeUrl(latestRef.current.video?.url);
        discardCharacters();
        setVideo(null);
        setThumbnails([]);
        setTrim({ start: 0, end: 0 });
        setPrompt("");
        setResolution("1080p");
        setAspectRatio("16:9");
        setErrorMessage("");
        setNotice("");
        setResult(null);
        setResultVideoFailed(false);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleClear = async () => {
        if (isLoading) return;
        const hasData = Boolean(video || prompt || result);
        if (hasData) {
            const confirmed = await confirmAction(
                "This will remove your video, characters, images and prompt.",
                "Clear all"
            );
            if (!confirmed) return;
        }
        resetAll();
    };

    /* =========================================================
       STEP 1 — VIDEO UPLOAD
    ========================================================= */
    const handleVideoSelect = async (file) => {
        if (!file || readingRef.current || submittingRef.current) return;

        if (!isVideoFile(file)) {
            showAlert("Only MP4 and WEBM videos are supported.");
            return;
        }
        if (file.size <= 0) {
            showAlert("This video file is empty.");
            return;
        }
        if (file.size > MAX_VIDEO_SIZE_MB * 1024 * 1024) {
            showAlert(
                `This video is ${formatSize(file.size)}. The maximum file size is ${MAX_VIDEO_SIZE_MB} MB.`
            );
            return;
        }
        if (latestRef.current.characters.length > 0) {
            const confirmed = await confirmAction(
                "Replacing the video will remove your trim selection, marked characters and reference images.",
                "Replace"
            );
            if (!confirmed) return;
        }

        readingRef.current = true;
        setIsReadingVideo(true);
        const url = URL.createObjectURL(file);
        try {
            const meta = await readVideoMetadata(url);
            if (!Number.isFinite(meta.duration) || meta.duration < MIN_TRIM_SECONDS) {
                revokeUrl(url);
                showAlert(
                    `The video must be at least ${MIN_TRIM_SECONDS} second long.`
                );
                return;
            }
            if (meta.duration > MAX_REFERENCE_VIDEO_SECONDS) {
                revokeUrl(url);
                showAlert(
                    `This video is ${formatTime(meta.duration)} long. The maximum length is ${MAX_REFERENCE_VIDEO_SECONDS / 60} minutes.`
                );
                return;
            }

            // Everything validated: now replace the previous video and its dependents.
            revokeUrl(latestRef.current.video?.url);
            discardCharacters();
            setNotice("");
            setErrorMessage("");
            setVideo({
                file,
                url,
                duration: meta.duration,
                width: meta.width,
                height: meta.height,
            });
            setTrim({
                start: 0,
                end: Math.min(MAX_TRIM_SECONDS, Math.floor(meta.duration)),
            });
        } catch {
            revokeUrl(url);
            showAlert(
                "This video could not be read. The file may be corrupted or use an unsupported codec."
            );
        } finally {
            readingRef.current = false;
            setIsReadingVideo(false);
        }
    };

    const handleVideoFiles = (fileList) => {
        const files = Array.from(fileList || []);
        if (!files.length) return;
        if (files.length > 1) {
            showAlert("Only one reference video is allowed. The first video will be used.");
        }
        handleVideoSelect(files[0]);
    };

    const handleVideoInput = (event) => {
        const files = Array.from(event.target.files || []);
        event.target.value = "";
        handleVideoFiles(files);
    };

    const handleVideoDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (isLoading || isReadingVideo) return;
        handleVideoFiles(event.dataTransfer?.files);
    };

    const handleRemoveVideo = async () => {
        if (isLoading) return;
        if (characters.length > 0) {
            const confirmed = await confirmAction(
                "Removing the video will also remove your marked characters and reference images.",
                "Remove"
            );
            if (!confirmed) return;
        }
        revokeUrl(latestRef.current.video?.url);
        discardCharacters();
        setVideo(null);
        setThumbnails([]);
        setTrim({ start: 0, end: 0 });
        setNotice("");
        setErrorMessage("");
    };

    const preventDefault = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };

    /* =========================================================
       STEP 2 — TRIM COMMIT
       Characters captured outside the new range are no longer valid.
    ========================================================= */
    const commitTrim = useCallback((next) => {
        const { characters: current, referenceImages: refs } = latestRef.current;
        const removed = current.filter(
            (character) =>
                character.timestamp < next.start - FRAME_TOLERANCE ||
                character.timestamp > next.end + FRAME_TOLERANCE
        );
        if (!removed.length) return;
        const removedIds = new Set(removed.map((character) => character.id));
        removed.forEach((character) => {
            revokeUrl(character.preview);
            revokeUrl(refs[character.id]?.preview);
        });
        setCharacters((previous) =>
            previous.filter((character) => !removedIds.has(character.id))
        );
        setReferenceImages((previous) => {
            const copy = { ...previous };
            removedIds.forEach((id) => delete copy[id]);
            return copy;
        });
        setNotice(
            `${removed.length} marked character${removed.length > 1 ? "s were" : " was"} removed (with the reference image) because ${removed.length > 1 ? "their frames are" : "its frame is"} outside the new trim range.`
        );
    }, []);

    /* =========================================================
       STEP 3 — CHARACTERS
    ========================================================= */
    const handleAddCharacter = useCallback((character) => {
        if (latestRef.current.characters.length >= MAX_CHARACTERS) {
            revokeUrl(character.preview);
            return;
        }
        setCharacters((previous) => [...previous, character]);
        setNotice("");
    }, []);

    const handleRemoveCharacter = useCallback((id) => {
        const { characters: current, referenceImages: refs } = latestRef.current;
        const target = current.find((character) => character.id === id);
        if (!target) return;
        revokeUrl(target.preview);
        revokeUrl(refs[id]?.preview);
        setCharacters((previous) => previous.filter((character) => character.id !== id));
        setReferenceImages((previous) => {
            const copy = { ...previous };
            delete copy[id];
            return copy;
        });
    }, []);

    /* =========================================================
       STEP 4 — REFERENCE IMAGES (one per character, keyed by id)
    ========================================================= */
    const handleReferenceFiles = (characterId, fileList) => {
        const files = Array.from(fileList || []);
        if (!files.length) return;
        const state = latestRef.current;
        if (!state.characters.some((character) => character.id === characterId)) return;

        if (files.length > 1) {
            showAlert(
                "Only one reference image can be uploaded for each character. The first image will be used."
            );
        }
        const file = files[0];
        if (!isImageFile(file)) {
            showAlert("Only PNG, JPG, JPEG and WEBP images are supported.");
            return;
        }
        if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
            showAlert(`Images must be ${MAX_IMAGE_SIZE_MB} MB or smaller.`);
            return;
        }
        revokeUrl(state.referenceImages[characterId]?.preview);
        setReferenceImages((previous) => ({
            ...previous,
            [characterId]: { file, preview: URL.createObjectURL(file) },
        }));
        setErrorMessage("");
    };

    const handleReferenceInput = (characterId, event) => {
        const files = Array.from(event.target.files || []);
        event.target.value = "";
        handleReferenceFiles(characterId, files);
    };

    const handleReferenceDrop = (characterId, event) => {
        event.preventDefault();
        event.stopPropagation();
        if (isLoading) return;
        handleReferenceFiles(characterId, event.dataTransfer?.files);
    };

    const handleRemoveReference = (characterId) => {
        revokeUrl(latestRef.current.referenceImages[characterId]?.preview);
        setReferenceImages((previous) => {
            const copy = { ...previous };
            delete copy[characterId];
            return copy;
        });
    };

    /* =========================================================
       STEP 7 — GENERATE
    ========================================================= */
    const getGenerationError = () => {
        for (let n = 1; n <= 6; n += 1) {
            const error = getStepError(n);
            if (error) return { error, step: n };
        }
        const ids = new Set(characters.map((character) => character.id));
        const mapped = Object.keys(referenceImages).filter((id) => ids.has(id));
        if (
            characters.length < 1 ||
            characters.length > MAX_CHARACTERS ||
            mapped.length !== characters.length ||
            Object.keys(referenceImages).length !== characters.length
        ) {
            return {
                error: "Each marked character needs exactly one reference image.",
                step: 4,
            };
        }
        if (!creditsKnown) {
            return {
                error: "Your credit balance could not be loaded. Please refresh the page and try again.",
                step: 7,
            };
        }
        if (!hasEnoughCredits) return { error: "NO_CREDITS", step: 7 };
        return null;
    };

    const buildFormData = () => {
        const formData = new FormData();
        formData.append("prompt", trimmedPrompt);

        // Reference images, in the same order as the characters.
        characters.forEach((character) => {
            const image = referenceImages[character.id];
            formData.append("imageUrls", image.file, image.file.name);
        });
        // Cropped character files, same order.
        characters.forEach((character, index) => {
            formData.append(
                "characterImages",
                character.file,
                `character-${index + 1}.jpg`
            );
        });
        formData.append(
            "characters",
            JSON.stringify(
                characters.map((character, index) => ({
                    id: character.id,
                    name: `Character ${index + 1}`,
                    index,
                    referenceImageIndex: index,
                    timestamp: character.timestamp,
                    relativeTimestamp: character.relativeTimestamp,
                    boundingBox: character.boundingBox,
                }))
            )
        );
        formData.append("videoUrls", video.file, video.file.name);
        formData.append("duration", String(selectedDuration));
        formData.append("resolution", resolution);
        formData.append("aspectRatio", aspectRatio);
        formData.append(
            "sessionName",
            trimmedPrompt.replace(/\s+/g, " ").slice(0, 70) || "Motion Video Session"
        );
        formData.append("startTime", String(trim.start));
        formData.append("endTime", String(trim.end));
        return formData;
    };

    const handleGenerate = async () => {
        if (submittingRef.current || isLoading) return;
        setErrorMessage("");

        const problem = getGenerationError();
        if (problem?.error === "NO_CREDITS") {
            swal({
                text: "You don't have enough credits! Please buy credits to continue.",
                icon: "error",
                buttons: "Buy",
                className: "modal_class_success",
            }).then((confirmed) => {
                if (confirmed) navigate("/purchase");
            });
            return;
        }
        if (problem) {
            setErrorMessage(problem.error);
            showAlert(problem.error, "error");
            return;
        }

        submittingRef.current = true;
        setIsLoading(true);
        setResult(null);

        try {
            /* Prompt checker — only for a non-empty prompt. */
            if (trimmedPrompt) {
                setLoadingStep("Checking your prompt...");
                const checkerResponse = await fetch(PROMPT_CHECK_ENDPOINT, {
                    method: "POST",
                    headers: {
                        ...authHeader(),
                        "Content-Type": "application/json",
                        Accept: "application/json",
                    },
                    body: JSON.stringify({
                        prompt: trimmedPrompt,
                        aiService: "motion-video-ai",
                        videoCount: 1,
                        imageCount: characters.length + characters.length,
                    }),
                });
                const checkerData = await checkerResponse.json().catch(() => null);
                if (
                    !checkerResponse.ok ||
                    !checkerData ||
                    checkerData.status === false ||
                    checkerData.success === false
                ) {
                    throw new Error(
                        checkerData?.message ||
                        checkerData?.error ||
                        "Prompt check failed. Please revise your prompt."
                    );
                }
            }

            /* Create the motion video (multipart/form-data). */
            setLoadingStep("Sending your files and starting motion video generation...");
            const response = await fetch(GENERATE_ENDPOINT, {
                method: "POST",
                // Do not set Content-Type: the browser adds the multipart boundary.
                headers: { ...authHeader(), Accept: "application/json" },
                body: buildFormData(),
            });
            const data = await response.json().catch(() => null);
            if (!response.ok || !data || data.status !== true) {
                throw new Error(
                    data?.message ||
                    data?.error ||
                    "Failed to start motion video generation."
                );
            }

            const job = {
                transactionId: data.transactionId || data.data?.transactionId || "",
                sessionId: data.sessionId || data.data?._id || data.data?.sessionId || "",
                duration: selectedDuration,
                resolution,
                aspectRatio,
                estimatedCredits,
                startedAt: new Date().toISOString(),
            };
            if (!job.transactionId && !job.sessionId) {
                refreshCredits();
                throw new Error(
                    "Generation started but no tracking ID was returned. Please check your usage history before trying again."
                );
            }

            updateActiveJob(job);
            setLoadingStep(
                data.message || "Processing video reference and generating motion video..."
            );

            // Normally the WebSocket delivers the result; handle an immediate one too.
            if (normalizeResult(data.data || data, job)) {
                finishJob(data.data || data, job);
            }
        } catch (error) {
            console.error("Motion Video AI error:", error?.message || error);
            const message =
                error?.message === "Failed to fetch"
                    ? "Network error. Please check your connection and try again."
                    : error?.message || "Something went wrong. Please try again.";
            submittingRef.current = false;
            setIsLoading(false);
            setLoadingStep("");
            setErrorMessage(message);
            swal({
                text: message,
                icon: "error",
                buttons: "Ok",
                className: "modal_class_success",
            });
        }
    };

    /* =========================================================
       STEP 9 — DOWNLOAD
    ========================================================= */
    const handleDownload = async () => {
        if (!result?.videoUrl || isDownloading) return;
        setIsDownloading(true);
        const filename = `motion-video-${Date.now()}.mp4`;
        try {
            const response = await fetch(result.videoUrl);
            if (!response.ok) throw new Error("Failed to download video.");
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
        } catch (error) {
            console.error("Video download failed:", error);
            // Fallback (e.g. CORS): open the video in a new tab.
            const link = document.createElement("a");
            link.href = result.videoUrl;
            link.target = "_blank";
            link.rel = "noopener noreferrer";
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
        } finally {
            setIsDownloading(false);
        }
    };

    const showHowItWorks = () =>
        showAlert(
            "1) Upload a reference video. 2) Trim up to 15 seconds. 3) Mark the characters. 4) Add one reference image per character. 5) Optionally write a prompt. 6) Choose settings. 7) Generate and wait for your result.",
            "info"
        );

    /* =========================================================
       RENDER
    ========================================================= */
    // const showForm = !result && !(isLoading && !video);
    // Form shows in all time
    const showForm = true;

    const renderUploadStep = () => (
        <div className="mv-step">
            <StepHeader number={1} title="Upload Reference Video" />
            <label className="mv-label">
                Reference Video <span className="mv-required">*</span>{" "}
                <span className="mv-label-note">
                    (required, one MP4 or WEBM video up to{" "}
                    {MAX_REFERENCE_VIDEO_SECONDS / 60} minutes)
                </span>
            </label>

            <input
                id="mv-video-input"
                type="file"
                accept="video/mp4,video/webm"
                className="mv-hidden-input"
                onChange={handleVideoInput}
                disabled={isLoading || isReadingVideo}
            />

            {!video ? (
                <label
                    htmlFor="mv-video-input"
                    className="mv-upload"
                    onDragOver={preventDefault}
                    onDrop={handleVideoDrop}
                >
                    <div className="mv-upload-orb">
                        <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.6"
                            width="25"
                            height="25"
                        >
                            <rect x="3" y="5" width="13" height="14" rx="2.5" />
                            <path d="m16 10 5-3v10l-5-3" />
                        </svg>
                    </div>
                    <div className="mv-upload-main">
                        <span>{isReadingVideo ? "Reading video..." : "Drop your video here"}</span>
                        {!isReadingVideo && (
                            <span className="mv-upload-browse">Browse</span>
                        )}
                    </div>
                    <div className="mv-upload-sub">
                        Upload the video whose movement you want to keep. Right
                        below you will choose a segment of up to{" "}
                        {MAX_TRIM_SECONDS} seconds.
                    </div>
                    <div className="mv-upload-meta">
                        <span>MP4</span>
                        <span>WEBM</span>
                    </div>
                </label>
            ) : (
                <div className="mv-video-card">
                    <video
                        src={video.url}
                        controls
                        playsInline
                        preload="metadata"
                    />
                    <div className="mv-video-bar">
                        <span className="mv-video-name" title={video.file.name}>
                            @Video1 · {video.file.name}
                        </span>
                        <span className="mv-video-actions">
                            <label
                                htmlFor="mv-video-input"
                                className="mv-link-btn"
                            >
                                Replace
                            </label>
                            <button
                                type="button"
                                className="mv-link-btn"
                                onClick={handleRemoveVideo}
                                disabled={isLoading}
                            >
                                Remove
                            </button>
                        </span>
                    </div>
                    <div className="mv-video-info">
                        <span>Length: {formatTime(video.duration)}</span>
                        <span>Size: {formatSize(video.file.size)}</span>
                        {video.width > 0 && (
                            <span>
                                {video.width}×{video.height}
                            </span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );

    const renderReferenceStep = () => (
        <div className="mv-step">
            <StepHeader number={4} title="Upload Reference Images" />
            <label className="mv-label">
                Reference Images <span className="mv-required">*</span>{" "}
                <span className="mv-label-note">
                    (exactly one image for each marked character)
                </span>
            </label>

            <div className="mv-ref-grid">
                {characters.map((character, index) => {
                    const reference = referenceImages[character.id];
                    const inputId = `mv-ref-input-${character.id}`;
                    return (
                        <div className="mv-ref-card" key={character.id}>
                            <div className="mv-ref-head">
                                <img
                                    src={character.preview}
                                    alt={`Character ${index + 1}`}
                                />
                                <div className="mv-ref-head-text">
                                    <strong>Character {index + 1}</strong>
                                    <span>
                                        @Image{index + 1} · frame at {character.timestamp}s
                                    </span>
                                </div>
                                <span
                                    className={`mv-ref-status ${reference ? "is-ready" : ""}`}
                                >
                                    {reference ? "Ready" : "Needed"}
                                </span>
                            </div>

                            <input
                                id={inputId}
                                type="file"
                                accept="image/png,image/jpeg,image/webp"
                                className="mv-hidden-input"
                                onChange={(event) =>
                                    handleReferenceInput(character.id, event)
                                }
                                disabled={isLoading}
                            />

                            {!reference ? (
                                <label
                                    htmlFor={inputId}
                                    className="mv-upload mv-ref-upload"
                                    onDragOver={preventDefault}
                                    onDrop={(event) =>
                                        handleReferenceDrop(character.id, event)
                                    }
                                >
                                    <div className="mv-upload-main">
                                        <span>Drop image for Character {index + 1}</span>
                                        <span className="mv-upload-browse">Browse</span>
                                    </div>
                                    <div className="mv-upload-meta">
                                        <span>JPG</span>
                                        <span>PNG</span>
                                        <span>WEBP</span>
                                    </div>
                                </label>
                            ) : (
                                <>
                                    <div className="mv-ref-filled">
                                        <img
                                            src={reference.preview}
                                            alt={`Reference for Character ${index + 1}`}
                                        />
                                        <span className="mv-image-number">
                                            @Image{index + 1}
                                        </span>
                                        <button
                                            type="button"
                                            className="mv-remove"
                                            onClick={() =>
                                                handleRemoveReference(character.id)
                                            }
                                            aria-label={`Remove image for Character ${index + 1}`}
                                            disabled={isLoading}
                                        >
                                            ×
                                        </button>
                                    </div>
                                    <label
                                        htmlFor={inputId}
                                        className="mv-link-btn mv-ref-replace"
                                    >
                                        Replace image
                                    </label>
                                </>
                            )}
                        </div>
                    );
                })}
            </div>

            <div className="mv-count">
                {uploadedReferenceCount}/{characters.length} reference images
                uploaded
            </div>
        </div>
    );

    const renderPromptStep = () => (
        <div className="mv-step">
            <StepHeader number={5} title="Write Prompt (Optional)" />
            <div className="mv-label-row">
                <label className="mv-label" htmlFor="mv-prompt">
                    Prompt{" "}
                    <span className="mv-label-note">
                        (optional — you can generate without one)
                    </span>
                </label>
                <button
                    type="button"
                    className="mv-clear"
                    onClick={() => setPrompt("")}
                    disabled={isLoading || !prompt}
                >
                    Clear
                </button>
            </div>

            <div className="mv-prompt-wrap">
                <textarea
                    id="mv-prompt"
                    className="mv-textarea"
                    value={prompt}
                    maxLength={MAX_PROMPT_LENGTH}
                    onChange={(event) => setPrompt(event.target.value)}
                    placeholder="Describe the video you want to generate. You can reference your reference images (@Image1, @Image2...) and @Video1."
                    disabled={isLoading}
                />
                <span className="mv-prompt-count">
                    {prompt.length}/{MAX_PROMPT_LENGTH}
                </span>
            </div>

            <p className="mv-hint">
                <span className="mv-hint-label">How to reference files:</span>{" "}
                {characters.map((_, index) => (
                    <Fragment key={index}>
                        <code>@Image{index + 1}</code>{" "}
                    </Fragment>
                ))}
                match your reference images in character order, and{" "}
                <code>@Video1</code> is the reference video. References are
                optional.{" "}
                <button
                    type="button"
                    className="mv-example-btn"
                    onClick={() => setPrompt(buildPromptExample(characters.length))}
                    disabled={isLoading}
                >
                    Use example
                </button>
            </p>
        </div>
    );

    const renderSettingsStep = () => (
        <div className="mv-step">
            <StepHeader number={6} title="Configure Video Settings" />
            <div className="mv-settings">
                <div className="mv-setting-card">
                    <label className="mv-label">
                        Duration (Seconds){" "}
                        <span className="mv-label-note">
                            (from your trim, max {MAX_TRIM_SECONDS}s)
                        </span>
                    </label>
                    <div className="mv-number is-readonly">{selectedDuration}</div>
                </div>

                <div className="mv-setting-card">
                    <label className="mv-label">
                        Resolution <span className="mv-required">*</span>
                    </label>
                    <div className="mv-chips">
                        {RESOLUTIONS.map((item) => (
                            <button
                                type="button"
                                key={item.value}
                                className={`mv-chip ${resolution === item.value ? "is-selected" : ""}`}
                                onClick={() => setResolution(item.value)}
                                disabled={isLoading}
                            >
                                <span className="mv-chip-top">{item.tag}</span>
                                <span className="mv-chip-bottom">{item.label}</span>
                            </button>
                        ))}
                    </div>
                </div>

                <div className="mv-setting-card">
                    <label className="mv-label">
                        Aspect Ratio <span className="mv-required">*</span>
                    </label>
                    <div className="mv-chips">
                        {ASPECTS.map((item) => (
                            <button
                                type="button"
                                key={item.value}
                                className={`mv-chip ${aspectRatio === item.value ? "is-selected" : ""}`}
                                onClick={() => setAspectRatio(item.value)}
                                disabled={isLoading}
                            >
                                <span className="mv-chip-top">{item.value}</span>
                                <span className="mv-chip-bottom">{item.label}</span>
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <p className="mv-hint">
                Estimated cost: {creditsPerSecond} credits/second ×{" "}
                {selectedDuration}s ={" "}
                <strong>{estimatedCredits.toLocaleString()} credits</strong>
            </p>
        </div>
    );

    const renderGenerateStep = () => (
        <div className="mv-step">
            <StepHeader number={7} title="Generate Video" />

            {/* <div className="mv-summary">
                <div className="mv-summary-row">
                    <span>Reference video</span>
                    <strong>
                        {video?.file.name} · {trim.start}s–{trim.end}s ({selectedDuration}s)
                    </strong>
                </div>
                <div className="mv-summary-row">
                    <span>Characters</span>
                    <div className="mv-summary-pairs">
                        {characters.map((character, index) => (
                            <div className="mv-summary-pair" key={character.id}>
                                <img src={character.preview} alt="" />
                                <span>→</span>
                                <img
                                    src={referenceImages[character.id]?.preview}
                                    alt=""
                                />
                                <span>
                                    Character {index + 1} = @Image{index + 1}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="mv-summary-row">
                    <span>Prompt</span>
                    <strong>{trimmedPrompt || "No prompt (optional)"}</strong>
                </div>
                <div className="mv-summary-row">
                    <span>Settings</span>
                    <strong>
                        {selectedDuration}s · {resolution} · {aspectRatio}
                    </strong>
                </div>
            </div> */}

            <div className="mv-generate-bar">
                <div className="mv-generate-info">
                    <span className="mv-generate-label">Estimated Cost</span>
                    <span className="mv-generate-value">
                        {estimatedCredits.toLocaleString()} Credits
                    </span>
                </div>
                <div className="mv-generate-actions">
                    <button
                        type="button"
                        className="mv-clear"
                        onClick={handleClear}
                        disabled={isLoading}
                    >
                        Clear
                    </button>
                    <button
                        type="button"
                        className="mv-generate-btn"
                        onClick={handleGenerate}
                        disabled={isLoading || maxReachable < 7 || !hasEnoughCredits}
                    >
                        {isLoading ? (
                            <span className="mv-spinner" aria-hidden="true" />
                        ) : (
                            <AutoAwesomeIcon fontSize="small" />
                        )}
                        {isLoading ? "Processing..." : "Generate Video"}
                    </button>
                </div>
            </div>

            {creditsKnown && !hasEnoughCredits && (
                <p className="mv-error mv-mt-2">
                    You need {estimatedCredits.toLocaleString()} credits, but you have{" "}
                    {availableCredits.toLocaleString()}.
                </p>
            )}

            <p className="mv-disclaimer">
                AI-generated content may occasionally contain mistakes. Please
                verify important information.
            </p>
        </div>
    );

    const renderProcessingStep = () => (
        <div className="mv-progress" role="status">
            <div className="mv-progress-orb">
                <AutoAwesomeIcon />
            </div>
            <p className="mv-progress-title">
                {loadingStep || "Generating your motion video..."}
            </p>
            <p className="mv-progress-text">
                This can take a few minutes. Your status updates automatically,
                and if you reload or leave this page your generation will be
                restored when you come back.
            </p>
        </div>
    );

    const renderResultStep = () =>
        result && (
            <div className="mv-result">
                <div className="mv-result-badge">
                    <span />
                    Generation Complete
                </div>
                <h4 className="mv-result-title">Your video is ready</h4>

                {isHttpUrl(result.videoUrl) && !resultVideoFailed ? (
                    <div className="mv-result-player">
                        <video
                            src={result.videoUrl}
                            controls
                            autoPlay
                            muted
                            playsInline
                            onError={() => setResultVideoFailed(true)}
                        />
                    </div>
                ) : (
                    <div className="mv-error" role="alert">
                        {isHttpUrl(result.videoUrl)
                            ? `The video could not be played here. Try downloading it instead. `
                            : "The video link returned by the server is invalid. Please check your usage history or contact support."}
                    </div>
                )}

                <div className="mv-result-grid">
                    <div className="mv-result-item">
                        <span>Duration</span>
                        <strong>{result.duration ? `${result.duration}s` : "—"}</strong>
                    </div>
                    <div className="mv-result-item">
                        <span>Resolution</span>
                        <strong>{result.resolution || "—"}</strong>
                    </div>
                    <div className="mv-result-item">
                        <span>Aspect Ratio</span>
                        <strong>{result.aspectRatio || "—"}</strong>
                    </div>
                    <div className="mv-result-item">
                        <span>Credits Used</span>
                        <strong>{result.credits || "—"}</strong>
                    </div>
                    <div className="mv-result-item">
                        <span>Status</span>
                        <strong>{result.status || "completed"}</strong>
                    </div>
                </div>

                {isHttpUrl(result.videoUrl) && (
                    <div className="mv-link-row">
                        <button
                            type="button"
                            className="mv-download-btn"
                            onClick={handleDownload}
                            disabled={isDownloading}
                        >
                            <DownloadIcon fontSize="small" />
                            {isDownloading ? "Preparing..." : "Download Video"}
                        </button>
                    </div>
                )}

                <button
                    type="button"
                    className="mv-secondary-btn mv-result-again"
                    onClick={resetAll}
                >
                    Create Another Video
                </button>
            </div>
        );

    return (
        <Fragment>
            <div className="mv-page">
                {/* Header */}
                <div className="mv-top">
                    <p className="mv-page-title">MOTIONMORPH AI</p>

                    <div className="credits-actions">
                        <button type="button" className="credits-button">
                            CREDITS : {credits}
                        </button>
                        <button
                            type="button"
                            className="buy-credits-button"
                            onClick={() => navigate("/purchase")}
                        >
                            BUY CREDITS
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate("/usage")}
                            className="money-exchange-button"
                            aria-label="View usage"
                        >
                            <img src={moneyExchangeIcon} alt="Usage" />
                        </button>
                        <button
                            type="button"
                            className="money-exchange-button"
                            aria-label="How it works"
                            onClick={showHowItWorks}
                        >
                            <img src={sampleIcon} alt="How it works" />
                        </button>
                    </div>
                </div>

                {/* Hero */}
                <section className="mv-hero">
                    <h1 className="mv-hero-title">
                        Transform Any Video.{" "}
                        <span className="mv-gradient-text">
                            Reimagine Every Character.
                        </span>
                    </h1>
                    <p className="mv-hero-sub">
                        Upload a motion-capture or reference video, replace up to{" "}
                        {MAX_CHARACTERS} characters with your chosen characters,
                        and preserve the original movement, camera angles, and
                        scene composition.
                    </p>
                </section>

                <div className="mv-container">
                    <div className="mv-section-head">
                        <h2 className="mv-section-title">
                            Swap Up to{" "}
                            <span className="mv-gradient-text">
                                {MAX_CHARACTERS} Characters
                            </span>
                        </h2>
                        <p className="mv-section-sub">
                            Upload a reference video, mark your characters, add
                            one reference image for each, then generate your
                            transformed video.
                        </p>
                    </div>

                    <div className="mv-layout">
                        <div className="mv-shell">
                            {showForm && (
                                <Fragment>
                                    {renderUploadStep()}

                                    {maxReachable >= 2 && video && (
                                        <TrimStep
                                            video={video}
                                            trim={trim}
                                            thumbnails={thumbnails}
                                            disabled={isLoading}
                                            onChange={setTrim}
                                            onCommit={commitTrim}
                                        />
                                    )}

                                    {maxReachable >= 3 && video && (
                                        <CharacterStep
                                            video={video}
                                            trim={trim}
                                            characters={characters}
                                            disabled={isLoading}
                                            onAdd={handleAddCharacter}
                                            onRemove={handleRemoveCharacter}
                                        />
                                    )}

                                    {notice && (
                                        <div className="mv-notice" role="status">
                                            {notice}
                                        </div>
                                    )}

                                    {maxReachable >= 4 && renderReferenceStep()}
                                    {maxReachable >= 5 && renderPromptStep()}
                                    {maxReachable >= 6 && renderSettingsStep()}
                                    {maxReachable >= 7 && renderGenerateStep()}

                                    {firstInvalidStep && !isLoading && !isReadingVideo && (
                                        <p className="mv-flow-hint">
                                            {getStepError(firstInvalidStep)}
                                        </p>
                                    )}
                                </Fragment>
                            )}

                            {/* {errorMessage && !isLoading && (
                                <div className="mv-error" role="alert" ref={errorRef}>
                                    {errorMessage}
                                </div>
                            )} */}

                            {isLoading && (
                                <div ref={progressRef}>{renderProcessingStep()}</div>
                            )}

                            {result && <div ref={resultRef}>{renderResultStep()}</div>}
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
}
